import { Box, Button, Typography } from '@material-ui/core';
import { createTheme, makeStyles, ThemeProvider } from '@material-ui/core/styles';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const unavailableTheme = createTheme();

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(6, 2),
  },
  iconWrap: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2.5),
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : theme.palette.grey[100],
  },
  icon: {
    fontSize: 36,
    color: theme.palette.text.secondary,
  },
  title: {
    marginBottom: theme.spacing(1),
    maxWidth: 480,
  },
  description: {
    maxWidth: 440,
    marginBottom: theme.spacing(3),
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
  },
  actionButton: {
    margin: theme.spacing(0.75),
  },
}));

export interface FeatureUnavailablePageProps {
  /** Optional product name, e.g. "Golden Paths". */
  featureName?: string;
}

/**
 * Shared empty state when a runtime feature flag is off.
 * Safe to reuse for any flagged feature page.
 */
export function FeatureUnavailablePage(props: FeatureUnavailablePageProps) {
  return (
    <ThemeProvider theme={unavailableTheme}>
      <FeatureUnavailableContent featureName={props.featureName} />
    </ThemeProvider>
  );
}

function FeatureUnavailableContent(props: FeatureUnavailablePageProps) {
  const classes = useStyles();
  const title = props.featureName
    ? `${props.featureName} is unavailable`
    : 'This feature is unavailable';

  return (
    <Box className={classes.root}>
      <Box className={classes.iconWrap} aria-hidden="true">
        <LockOutlinedIcon className={classes.icon} />
      </Box>
      <Typography variant="h5" className={classes.title}>
        {title}
      </Typography>
      <Typography
        variant="body1"
        color="textSecondary"
        className={classes.description}
      >
        An administrator has turned this feature off. If you need access,
        contact a Backstage admin.
      </Typography>
      <Box className={classes.actions}>
        <Button
          className={classes.actionButton}
          variant="outlined"
          color="default"
          startIcon={<ArrowBackIcon />}
          onClick={() => window.history.back()}
        >
          Go back
        </Button>
        <Button
          className={classes.actionButton}
          href="/dashboard"
          variant="contained"
          color="primary"
          startIcon={<HomeOutlinedIcon />}
        >
          Go to Home
        </Button>
      </Box>
    </Box>
  );
}
